import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { productsAPI, formulasAPI, aiAPI, recipeIngredientsAPI } from '@/utils/api';
import { Plus, Trash2, AlertTriangle, CheckCircle2, Sparkles, Loader2, FileDown, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import DipBreadcrumb from '@/components/DipBreadcrumb';
import DipStepFooter from '@/components/DipStepFooter';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Formula = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [formula, setFormula] = useState(null);
  const [ingredients, setIngredients] = useState([
    { french_name: '', inci_name: '', percentage: '', function: '', cas_number: '' },
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [translatingIndex, setTranslatingIndex] = useState(null);
  const [casLoadingIndex, setCasLoadingIndex] = useState(null);
  const [aiSuggestionText, setAiSuggestionText] = useState('');
  const [showAiDialog, setShowAiDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, [productId]);

  const loadData = async () => {
    try {
      const prodRes = await productsAPI.getOne(productId);
      setProduct(prodRes.data);

      let dipFormulaLoaded = false;
      try {
        const formRes = await formulasAPI.getByProduct(productId);
        if (formRes.data && Array.isArray(formRes.data.ingredients) && formRes.data.ingredients.length > 0) {
          setFormula(formRes.data);
          setIngredients(formRes.data.ingredients);
          dipFormulaLoaded = true;
        }
      } catch (error) {
        console.log('No existing DIP formula');
      }

      // Fallback: load from Production recipe (recipe_compositions)
      if (!dipFormulaLoaded) {
        try {
          const recipeRes = await recipeIngredientsAPI.getByDipProduct(productId);
          if (recipeRes.data && recipeRes.data.length > 0) {
            const mapped = recipeRes.data.map(r => ({
              french_name: r.material_name || '',
              inci_name: r.inci_name || '',
              percentage: r.percentage ? String(r.percentage) : '',
              function: '',
              cas_number: '',
            }));
            setIngredients(mapped);
          }
        } catch (e) {
          console.log('No production recipe linked');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const addIngredient = () => {
    setIngredients([...ingredients, { french_name: '', inci_name: '', percentage: '', function: '', cas_number: '' }]);
  };

  const handleTranslateToInci = async (index, frenchName) => {
    if (!frenchName.trim()) return;
    
    setTranslatingIndex(index);
    try {
      const res = await aiAPI.translateToInci(frenchName);
      const updated = [...ingredients];
      updated[index].inci_name = res.data.inci_name;
      setIngredients(updated);
      toast.success('Nom INCI trouvé');
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Erreur lors de la traduction INCI');
    } finally {
      setTranslatingIndex(null);
    }
  };

  const removeIngredient = (index) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...ingredients];
    updated[index][field] = value;
    setIngredients(updated);
  };

  const handleSave = async () => {
    const validIngredients = ingredients.filter((ing) => ing.inci_name && ing.percentage);

    if (validIngredients.length === 0) {
      toast.error('Ajoutez au moins un ingrédient valide');
      return;
    }

    setSaving(true);
    try {
      const data = {
        product_id: productId,
        ingredients: validIngredients.map((ing) => ({
          ...ing,
          percentage: parseFloat(ing.percentage),
        })),
      };

      if (formula) {
        await formulasAPI.update(formula.id, data);
        toast.success('Formule mise à jour');
      } else {
        await formulasAPI.create(data);
        toast.success('Formule créée avec succès');
      }

      loadData();
    } catch (error) {
      console.error('Error saving formula:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAISuggestion = async () => {
    setAiLoading(true);
    try {
      const context = `Produit: ${product.name} (${product.type})`;
      const query = 'Suggère-moi 5 ingrédients naturels pour cette formule avec leurs pourcentages indicatifs et leur fonction. Formate la réponse en liste claire.';
      const res = await aiAPI.suggestIngredients({ query, context });
      const text = res?.data?.suggestions || '';
      if (!text || text.trim().length === 0) {
        toast.error('Aucune suggestion reçue de l\'IA');
        return;
      }
      setAiSuggestionText(text);
      setShowAiDialog(true);
    } catch (error) {
      console.error('AI Error:', error);
      toast.error('Erreur lors de la consultation de l\'IA');
    } finally {
      setAiLoading(false);
    }
  };

  const handleFindCas = async (index) => {
    const ing = ingredients[index];
    const lookup = (ing.inci_name || ing.french_name || '').trim();
    if (!lookup) {
      toast.error('Renseignez d\'abord le nom INCI ou français');
      return;
    }
    setCasLoadingIndex(index);
    try {
      const res = await aiAPI.findCas(lookup);
      const cas = (res?.data?.cas_number || '').trim();
      if (!cas) {
        toast.warning('Aucun numéro CAS trouvé pour cet ingrédient');
        return;
      }
      const updated = [...ingredients];
      updated[index].cas_number = cas;
      setIngredients(updated);
      toast.success(`N° CAS trouvé : ${cas}`);
    } catch (error) {
      console.error('CAS lookup error:', error);
      toast.error('Erreur lors de la recherche du N° CAS');
    } finally {
      setCasLoadingIndex(null);
    }
  };

  const totalPercentage = ingredients.reduce((sum, ing) => {
    const val = parseFloat(ing.percentage) || 0;
    return sum + val;
  }, 0);

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton-shimmer h-64 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <DipBreadcrumb productId={productId} productName={product?.name} currentStep="formula" />
      <div className="flex items-center justify-end mb-6">
        {formula && ingredients.length > 0 && (
          <Button variant="outline" className="gap-2" data-testid="btn-export-formula-pdf"
            onClick={() => window.open(`${API_BASE}/api/formulas/${productId}/pdf`, '_blank')}>
            <FileDown size={18} /> Imprimer PDF
          </Button>
        )}
      </div>

      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">
          Formule cosmétique
        </h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          {product?.name} - Composition INCI
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Ingrédients INCI</CardTitle>
              <CardDescription>Listez tous les ingrédients selon la nomenclature INCI</CardDescription>
            </div>
            <Button
              data-testid="btn-ai-suggest"
              variant="outline"
              onClick={handleAISuggestion}
              disabled={aiLoading}
              className="gap-2"
            >
              {aiLoading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  IA en cours...
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  Suggestions IA
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {ingredients.map((ingredient, index) => (
            <div key={index} className="space-y-3 p-4 border border-border rounded-lg">
              <div className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                    Nom en français
                  </label>
                  <div className="flex gap-2">
                    <Input
                      data-testid={`input-french-${index}`}
                      placeholder="Ex: Eau, Glycérine, Aloe vera..."
                      value={ingredient.french_name}
                      onChange={(e) => updateIngredient(index, 'french_name', e.target.value)}
                      onBlur={(e) => {
                        if (e.target.value && !ingredient.inci_name) {
                          handleTranslateToInci(index, e.target.value);
                        }
                      }}
                    />
                    <Button
                      data-testid={`btn-translate-${index}`}
                      variant="outline"
                      size="icon"
                      onClick={() => handleTranslateToInci(index, ingredient.french_name)}
                      disabled={translatingIndex === index || !ingredient.french_name}
                      className="flex-shrink-0"
                    >
                      {translatingIndex === index ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        <Sparkles size={18} />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="col-span-6">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
                    Nom INCI
                    {translatingIndex === index && (
                      <span className="text-accent text-xs normal-case">Traduction en cours...</span>
                    )}
                  </label>
                  <Input
                    data-testid={`input-inci-${index}`}
                    placeholder="Ex: Aqua, Glycerin, Aloe Barbadensis Leaf Juice..."
                    value={ingredient.inci_name}
                    onChange={(e) => updateIngredient(index, 'inci_name', e.target.value)}
                  />
                </div>
                <div className="col-span-1">
                  {ingredients.length > 1 && (
                    <Button
                      data-testid={`btn-remove-ingredient-${index}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => removeIngredient(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 size={18} />
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-3">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                    Pourcentage (%)
                  </label>
                  <Input
                    data-testid={`input-percentage-${index}`}
                    type="number"
                    step="0.01"
                    placeholder="10.5"
                    value={ingredient.percentage}
                    onChange={(e) => updateIngredient(index, 'percentage', e.target.value)}
                  />
                </div>
                <div className="col-span-6">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                    Fonction
                  </label>
                  <Input
                    data-testid={`input-function-${index}`}
                    placeholder="Ex: Hydratant, Émollient, Conservateur..."
                    value={ingredient.function}
                    onChange={(e) => updateIngredient(index, 'function', e.target.value)}
                  />
                </div>
                <div className="col-span-3">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                    N° CAS
                  </label>
                  <div className="flex gap-2">
                    <Input
                      data-testid={`input-cas-${index}`}
                      placeholder="000-00-0"
                      value={ingredient.cas_number}
                      onChange={(e) => updateIngredient(index, 'cas_number', e.target.value)}
                    />
                    <Button
                      data-testid={`btn-find-cas-${index}`}
                      variant="outline"
                      size="icon"
                      onClick={() => handleFindCas(index)}
                      disabled={casLoadingIndex === index || (!ingredient.inci_name && !ingredient.french_name)}
                      title="Trouver le N° CAS automatiquement via IA"
                      className="flex-shrink-0"
                    >
                      {casLoadingIndex === index ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        <FlaskConical size={18} />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <Button
            data-testid="btn-add-ingredient"
            variant="outline"
            onClick={addIngredient}
            className="w-full gap-2 mt-4"
          >
            <Plus size={18} />
            Ajouter un ingrédient
          </Button>

          <div className="flex items-center justify-between p-4 bg-muted rounded-md mt-6">
            <span className="font-semibold text-foreground">Total</span>
            <span
              className={`text-lg font-semibold ${
                Math.abs(totalPercentage - 100) < 0.5 ? 'text-green-600' : 'text-amber-600'
              }`}
            >
              {totalPercentage.toFixed(2)}%
            </span>
          </div>
        </CardContent>
      </Card>

      {formula?.compliance_check && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {formula.compliance_check.compliant ? (
                <CheckCircle2 className="text-green-600" size={24} />
              ) : (
                <AlertTriangle className="text-amber-600" size={24} />
              )}
              Vérification de conformité
            </CardTitle>
          </CardHeader>
          <CardContent>
            {formula.compliance_check.alerts.length === 0 ? (
              <p className="text-green-600 font-medium">Aucune alerte. Formule conforme.</p>
            ) : (
              <div className="space-y-3">
                {formula.compliance_check.alerts.map((alert, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-md border ${
                      alert.severity === 'high'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}
                  >
                    <p className="font-medium text-sm">{alert.ingredient}</p>
                    <p className="text-sm text-muted-foreground">{alert.issue}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="ghost" onClick={() => navigate('/products')}>
          Annuler
        </Button>
        <Button data-testid="btn-save-formula" onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer la formule'}
        </Button>
      </div>

      <DipStepFooter productId={productId} currentStep="formula" />

      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent data-testid="ai-suggestions-dialog" className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={20} className="text-accent" />
              Suggestions IA
            </DialogTitle>
            <DialogDescription>
              Ingrédients naturels suggérés pour {product?.name}. Recopiez ceux qui vous intéressent dans la formule.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-foreground bg-muted/40 p-4 rounded-md border border-border">
            {aiSuggestionText}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              data-testid="btn-copy-ai-suggestions"
              onClick={() => {
                navigator.clipboard.writeText(aiSuggestionText);
                toast.success('Suggestions copiées dans le presse-papier');
              }}
            >
              Copier
            </Button>
            <Button data-testid="btn-close-ai-dialog" onClick={() => setShowAiDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Formula;
